import { useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSnackbar from "components/MDSnackbar";



import { RequestService } from "services/RequestService";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";

const validatePassword = (password) => {
  const errors = [];
  
  // Password validation rules
  if (password.length < 6) {
    errors.push("يجب أن تحتوي كلمة المرور على 6 أحرف على الأقل");
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على حرف كبير واحد على الأقل (A-Z)");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على حرف صغير واحد على الأقل (a-z)");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على رقم واحد على الأقل (0-9)");
  }
  if (!/[^A-Za-z0-9]/.test(password)) {
    errors.push("يجب أن تحتوي كلمة المرور على رمز خاص واحد على الأقل (!@#$%^&*...)");
  }
  
  return errors;
};

function ChangePassword() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const navigate = useNavigate();

  const showNotification = (message, type = "success") => {
    setNotification({
      show: true,
      message,
      type,
    });
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, show: false });
  };

  const handleChangePassword = async () => {
    // Validate new password
    const errors = validatePassword(newPassword);
    setPasswordErrors(errors);

    // Check if passwords match
    if (newPassword !== confirmNewPassword) {
      setConfirmPasswordError("كلمات المرور غير متطابقة");
      return;
    }

    if (errors.length > 0) {
      return;
    }

    try {
      const response = await RequestService.post("Auth/change-password", {
        oldPassword: currentPassword,
        newPassword: newPassword,
      });

      if (response.data && !response.data.error) {
        showNotification("تم تغيير كلمة المرور بنجاح", "success");
        // Reset form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
        setPasswordErrors([]);
        setConfirmPasswordError("");
        
        // Remove authentication cookie
        Cookies.remove("Authentication");
        
        // Wait for 1 second before redirecting
        setTimeout(() => {
          navigate("/authentication/sign-in");
        }, 1000);
      } else {
        showNotification(response.data.message || "حدث خطأ أثناء تغيير كلمة المرور", "error");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showNotification("حدث خطأ أثناء تغيير كلمة المرور", "error");
    }
  };

  return (
    <Card id="change-password" sx={{ overflow: "visible" }}>
      <MDBox
        p={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        backgroundColor="info.main"
        color="white"
        borderRadius="lg"
        mb={3}
      >
        <MDBox>
          <MDTypography variant="h5" fontWeight="medium" color="white">
            تغيير كلمة المرور
          </MDTypography>
          <MDTypography variant="body2" color="white" opacity={0.8}>
            قم بتحديث كلمة المرور الخاصة بك بشكل دوري لضمان الأمان
          </MDTypography>
        </MDBox>
        <MDButton 
          variant="outlined" 
          color="white"
          circular
          iconOnly
          sx={{
            border: '2px solid white',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '2px solid white',
            }
          }}
        >
          <Icon>lock</Icon>
        </MDButton>
      </MDBox>
      <MDBox component="form" pb={3} px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <MDBox mb={2} position="relative">
              <MDInput
                fullWidth
                label="كلمة المرور الحالية"
                inputProps={{
                  type: "password",
                  autoComplete: "current-password",
                }}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "xl",
                    "&:hover fieldset": {
                      borderColor: "info.main",
                      borderWidth: "2px"
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "text.main",
                  },
                  "& .MuiInputBase-input": {
                    color: "text.main",
                  }
                }}
              />
              <MDButton
                variant="text"
                color="info"
                size="small"
                iconOnly
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <Icon>lock_open</Icon>
              </MDButton>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <MDBox mb={2} position="relative">
              <MDInput
                fullWidth
                label="كلمة المرور الجديدة"
                inputProps={{
                  type: "password",
                  autoComplete: "new-password",
                }}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setPasswordErrors(validatePassword(e.target.value));
                  if (confirmNewPassword && e.target.value !== confirmNewPassword) {
                    setConfirmPasswordError("كلمات المرور غير متطابقة");
                  } else {
                    setConfirmPasswordError("");
                  }
                }}
                error={passwordErrors.length > 0}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "xl",
                    "&:hover fieldset": {
                      borderColor: "info.main",
                      borderWidth: "2px"
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "text.main",
                  },
                  "& .MuiInputBase-input": {
                    color: "text.main",
                  }
                }}
              />
              <MDButton
                variant="text"
                color="info"
                size="small"
                iconOnly
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <Icon>lock_open</Icon>
              </MDButton>
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <MDBox mb={2} position="relative">
              <MDInput
                fullWidth
                label="تأكيد كلمة المرور الجديدة"
                inputProps={{
                  type: "password",
                  autoComplete: "new-password",
                }}
                value={confirmNewPassword}
                onChange={(e) => {
                  setConfirmNewPassword(e.target.value);
                  if (e.target.value !== newPassword) {
                    setConfirmPasswordError("كلمات المرور غير متطابقة");
                  } else {
                    setConfirmPasswordError("");
                  }
                }}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "xl",
                    "&:hover fieldset": {
                      borderColor: "info.main",
                      borderWidth: "2px"
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "text.main",
                  },
                  "& .MuiInputBase-input": {
                    color: "text.main",
                  }
                }}
              />
              <MDButton
                variant="text"
                color="info"
                size="small"
                iconOnly
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <Icon>lock_open</Icon>
              </MDButton>
            </MDBox>
          </Grid>
          {(passwordErrors.length > 0 || confirmPasswordError) && (
            <Grid item xs={12}>
              <MDBox 
                p={3} 
                borderRadius="xl" 
                bgColor="grey.100"
                border="1px solid"
                borderColor="info.main"
                boxShadow="0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)"
              >
                <MDBox display="flex" alignItems="center" mb={2}>
                  <Icon sx={{ color: 'info.main', marginLeft: 1 }}>info</Icon>
                  <MDTypography variant="h6" color="info" fontWeight="medium">
                    متطلبات كلمة المرور
                  </MDTypography>
                </MDBox>
                {passwordErrors.map((error, index) => (
                  <MDBox 
                    key={index} 
                    display="flex" 
                    alignItems="center" 
                    mb={1}
                    sx={{
                      backgroundColor: 'rgba(255,0,0,0.05)',
                      borderRadius: 'lg',
                      padding: '8px 16px',
                    }}
                  >
                    <Icon sx={{ color: 'error.main', marginLeft: 1, fontSize: '1rem' }}>cancel</Icon>
                    <MDTypography
                      variant="button"
                      color="error"
                      fontWeight="regular"
                    >
                      {error}
                    </MDTypography>
                  </MDBox>
                ))}
                {confirmPasswordError && (
                  <MDBox 
                    display="flex" 
                    alignItems="center"
                    sx={{
                      backgroundColor: 'rgba(255,0,0,0.05)',
                      borderRadius: 'lg',
                      padding: '8px 16px',
                    }}
                  >
                    <Icon sx={{ color: 'error.main', marginLeft: 1, fontSize: '1rem' }}>cancel</Icon>
                    <MDTypography
                      variant="button"
                      color="error"
                      fontWeight="regular"
                    >
                      {confirmPasswordError}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Grid>
          )}
        </Grid>
        <MDBox mt={4} mb={1} display="flex" justifyContent="flex-start" gap={2}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleChangePassword}
            disabled={
              !currentPassword ||
              !newPassword ||
              !confirmNewPassword ||
              passwordErrors.length > 0 ||
              !!confirmPasswordError
            }
            sx={{
              px: 6,
              py: 1.5,
              borderRadius: "xl",
              fontSize: "0.875rem",
              fontWeight: "bold",
              textTransform: "none",
              "&:disabled": {
                opacity: 0.7,
                backgroundColor: "info.main",
                color: "white",
                "& .material-icons": {
                  color: "white"
                }
              },
              "&:not(:disabled):hover": {
                transform: "translateY(-1px)",
                boxShadow: "lg",
                backgroundColor: "info.dark"
              }
            }}
          >
            <Icon sx={{ marginLeft: 1, color: "white" }}>save</Icon>
            <MDTypography variant="button" color="white" fontWeight="medium">
              حفظ التغييرات
            </MDTypography>
          </MDButton>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => {
              setCurrentPassword("");
              setNewPassword("");
              setConfirmNewPassword("");
              setPasswordErrors([]);
              setConfirmPasswordError("");
            }}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: "xl",
              fontSize: "0.875rem",
              fontWeight: "bold",
              textTransform: "none",
              borderWidth: "2px",
              "&:hover": {
                borderWidth: "2px",
                backgroundColor: "transparent",
                transform: "translateY(-1px)"
              }
            }}
          >
            <Icon sx={{ marginLeft: 1, color: "info.main" }}>refresh</Icon>
            <MDTypography variant="button" color="info" fontWeight="medium">
              إعادة تعيين
            </MDTypography>
          </MDButton>
        </MDBox>
      </MDBox>
      <MDSnackbar
        color={notification.type}
        icon={notification.type === "success" ? "check" : "warning"}
        title="لوحة ادارة ربان السفينة"
        content={notification.message}
        open={notification.show}
        onClose={handleCloseNotification}
        close={handleCloseNotification}
        bgWhite
      />
    </Card>
  );
}

export default ChangePassword;
